// The calculation editor of the Visual designer (Layout → ƒx New calculation,
// or the pencil on a field of the Calculations folder).
//
// A problem is never silent: the field it belongs to turns red with the
// reason under it, the formula's problem is marked IN a copy of the formula
// ("[Gross margin] / «[Net sale]» * 100"), and Save on a dialog with problems
// keeps the dialog open, says how many there are, and puts the cursor on the
// first one with the bad part selected. The language and its SQL live in
// calculations.ts.

import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, Check, Info } from 'lucide-react'
import Modal from '@/components/common/Modal'
import Button from '@/components/common/Button'
import type { BankColumn } from './dataSource'
import {
  CALC_STARTERS, FX_FUNCTIONS, compileCalculation, markProblem,
  type CalcWhen, type VisualCalculation,
} from './calculations'
import { fieldGlyph } from './FieldTree'

export interface CalcDraft {
  /** '' for a new calculation. */
  key: string
  label: string
  formula: string
  when: CalcWhen
}

const WHENS: Array<{ id: CalcWhen; label: string; help: string }> = [
  { id: 'total', label: 'After totals',
    help: 'Adds up each field first, then works the formula out — right for ratios and percentages (a region’s Margin % = its margin ÷ its sales).' },
  { id: 'row', label: 'Row by row',
    help: 'Works the formula out on every record, then adds the results up — right for amounts such as Quantity × Rate.' },
]

const OPS: Array<[string, string, string]> = [
  ['+', ' + ', 'Add'], ['−', ' - ', 'Subtract'], ['×', ' * ', 'Multiply'], ['÷', ' / ', 'Divide'],
  ['(', '(', 'Open bracket'], [')', ')', 'Close bracket'],
]

export default function CalculationDialog({
  draft, bank, calcs, readOnly, onSave, onDelete, onClose,
}: {
  draft: CalcDraft
  /** The data source's own fields (no calculations). */
  bank: BankColumn[]
  /** Every calculation of the visual, as saved. */
  calcs: VisualCalculation[]
  readOnly: boolean
  /** Returns a reason when the save could not be made ('' / void = done). */
  onSave: (calc: CalcDraft, placeNow: boolean) => string | void
  /** Returns a reason when it could not be deleted ('' / void = done). */
  onDelete: (key: string) => string | void
  onClose: () => void
}) {
  const isNew = !draft.key
  const [label, setLabel] = useState(draft.label)
  const [formula, setFormula] = useState(draft.formula)
  const [when, setWhen] = useState<CalcWhen>(draft.when)
  const [placeNow, setPlaceNow] = useState(true)
  const [tried, setTried] = useState(false)
  const [footError, setFootError] = useState('')
  const nameRef = useRef<HTMLInputElement>(null)
  const fxRef = useRef<HTMLTextAreaElement>(null)
  useEffect(() => { (isNew ? nameRef : fxRef).current?.focus() }, [isNew])

  const check = useMemo(
    () => compileCalculation({ key: draft.key, label, formula, when }, bank, calcs),
    [draft.key, label, formula, when, bank, calcs],
  )
  const hasFormula = !!formula.trim()

  // ── problems, each on the field it belongs to ──────────────────
  const nameProblem = (() => {
    const l = label.trim()
    if (!l) return tried ? 'Give the calculation a name' : ''
    if (/[[\]]/.test(l)) return 'A name can’t contain [ or ] — formulas use them around names'
    const lc = l.toLowerCase()
    const field = bank.find((b) => (b.label || b.name).trim().toLowerCase() === lc)
    if (field) return `There is already a field called ${field.label || field.name} — pick another name`
    const other = calcs.find((c) => c.key !== draft.key && c.label.trim().toLowerCase() === lc)
    if (other) return `There is already a calculation called ${other.label} — pick another name`
    return ''
  })()
  const fxProblem = !check.ok && (hasFormula || tried) ? check.problem : null
  const mark = fxProblem && hasFormula ? markProblem(formula, fxProblem.at) : null
  const problemCount = (nameProblem ? 1 : 0) + (fxProblem ? 1 : 0)
  const footMessage = footError
    || (tried && problemCount ? (problemCount === 1 ? 'Fix the problem marked above to save' : `Fix the ${problemCount} problems marked above to save`) : '')

  // ── inserting at the cursor ────────────────────────────────────
  const insert = (text: string) => {
    if (readOnly) return
    const el = fxRef.current
    const start = el ? el.selectionStart : formula.length
    const end = el ? el.selectionEnd : formula.length
    const before = formula.slice(0, start)
    const pad = before && !/[\s(]$/.test(before) && !/^\s/.test(text) && text !== ')' ? ' ' : ''
    const next = before + pad + text + formula.slice(end)
    setFormula(next)
    setFootError('')
    const caret = (before + pad + text).length
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(caret, caret) })
  }

  const fieldsToInsert = useMemo(() => {
    const own = bank.filter((b) => b.DBNAME && (b.type === 'number' || /COUNT/i.test(b.defaultFunction || '')))
    const others = calcs.filter((c) => c.key !== draft.key)
    return { own, others }
  }, [bank, calcs, draft.key])

  const labelsLc = useMemo(() => new Set([
    ...bank.map((b) => (b.label || b.name).trim().toLowerCase()),
    ...calcs.map((c) => c.label.trim().toLowerCase()),
  ]), [bank, calcs])
  const starters = isNew
    ? CALC_STARTERS.filter((s) => !labelsLc.has(s.label.toLowerCase())
        && compileCalculation({ key: '', label: s.label, formula: s.formula, when: s.when }, bank, calcs).ok)
    : []

  const save = () => {
    if (readOnly) return
    setTried(true)
    setFootError('')
    const nameBad = !label.trim() || !!nameProblem
    if (nameBad || !check.ok) {
      requestAnimationFrame(() => {
        if (nameBad) { nameRef.current?.focus(); return }
        const el = fxRef.current
        if (!el) return
        el.focus()
        const at = !check.ok ? check.problem.at : null
        if (at) el.setSelectionRange(at[0], Math.max(at[0], at[1]))
      })
      return
    }
    const why = onSave({ key: draft.key, label: label.trim(), formula: formula.trim(), when }, isNew && placeNow)
    if (why) setFootError(why)
  }

  const remove = () => {
    if (readOnly || isNew) return
    const why = onDelete(draft.key)
    if (why) setFootError(why)
  }

  const whenHelp = (WHENS.find((w) => w.id === when) || WHENS[0]).help

  return (
    <Modal
      open
      onClose={onClose}
      title={isNew ? 'New calculation' : 'Edit calculation'}
      size="wide"
      footer={(
        <div className="vdo-cd-foot">
          {!isNew && !readOnly && <Button variant="ghost" size="sm" onClick={remove} className="vdo-cd-del">Delete</Button>}
          {isNew && !readOnly && (
            <label className="vdo-cd-place">
              <input type="checkbox" checked={placeNow} onChange={(e) => setPlaceNow(e.target.checked)} />
              Add it to the visual now
            </label>
          )}
          <span style={{ flex: '1 1 auto' }} />
          {footMessage && (
            <span className="vdo-cd-ferr" role="alert"><AlertCircle size={14} aria-hidden="true" />{footMessage}</span>
          )}
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          {!readOnly && <Button size="sm" onClick={save}>{isNew ? 'Add calculation' : 'Save changes'}</Button>}
        </div>
      )}
    >
      <div className="vdo-cd">
        {starters.length > 0 && (
          <>
            <div className="vdo-cd-lbl" style={{ marginTop: 0 }}>Start from</div>
            <div className="vdo-cd-starters">
              {starters.map((s) => (
                <button key={s.label} type="button" className="vdo-cd-st" title={s.formula}
                  onClick={() => { setLabel(s.label); setFormula(s.formula); setWhen(s.when); setFootError('') }}>
                  <b>{s.label}</b><span>{s.about}</span>
                </button>
              ))}
            </div>
          </>
        )}

        <label className="vdo-cd-lbl" htmlFor="vdo-calc-name">Name</label>
        <input
          id="vdo-calc-name"
          ref={nameRef}
          className={`vdo-cd-input${nameProblem ? ' bad' : ''}`}
          value={label}
          readOnly={readOnly}
          placeholder="e.g. Margin %"
          aria-invalid={!!nameProblem}
          aria-describedby={nameProblem ? 'vdo-calc-name-err' : undefined}
          onChange={(e) => { setLabel(e.target.value); setFootError('') }}
        />
        {nameProblem && (
          <p className="vdo-cd-err" id="vdo-calc-name-err" role="alert">
            <AlertCircle size={14} aria-hidden="true" /><span>{nameProblem}</span>
          </p>
        )}

        <label className="vdo-cd-lbl" htmlFor="vdo-calc-fx">Formula</label>
        <textarea
          id="vdo-calc-fx"
          ref={fxRef}
          className={`vdo-cd-fx${fxProblem ? ' bad' : ''}`}
          spellCheck={false}
          value={formula}
          readOnly={readOnly}
          placeholder="[Gross margin] / [Net sales] * 100"
          aria-invalid={!!fxProblem}
          aria-describedby="vdo-calc-check"
          onChange={(e) => { setFormula(e.target.value); setFootError('') }}
        />
        {!readOnly && (
          <>
            <div className="vdo-cd-ins">
              <span className="vdo-cd-meta">Click to add — fields go in [square brackets]</span>
              {fieldsToInsert.others.map((c) => (
                <button key={c.key} type="button" className="vdo-cd-f" title={`${c.label} = ${c.formula}`}
                  onClick={() => insert(`[${c.label}]`)}>
                  <span className="vdo-glyph vdo-g-calc" aria-hidden="true">ƒx</span>{c.label}
                </button>
              ))}
              {fieldsToInsert.own.map((b) => {
                const g = fieldGlyph(b)
                return (
                  <button key={b.key} type="button" className="vdo-cd-f" title={`Insert [${b.label || b.name}]`}
                    onClick={() => insert(`[${b.label || b.name}]`)}>
                    <span className={`vdo-glyph ${g.cls}`} aria-hidden="true">{g.text}</span>{b.label || b.name}
                  </button>
                )
              })}
              {!fieldsToInsert.own.length && !fieldsToInsert.others.length && (
                <span className="vdo-cd-meta">This data source has no number fields to calculate with.</span>
              )}
            </div>
            <div className="vdo-cd-ins" style={{ marginTop: 6 }}>
              {OPS.map(([t, ins, tip]) => (
                <button key={t} type="button" className="vdo-cd-op" title={tip} aria-label={tip} onClick={() => insert(ins.trimStart())}>{t}</button>
              ))}
              <span style={{ width: 8 }} />
              {Object.entries(FX_FUNCTIONS).map(([name, spec]) => (
                <button key={name} type="button" className="vdo-cd-op fn" title={spec.help} onClick={() => insert(`${name}(`)}>{name}</button>
              ))}
            </div>
          </>
        )}

        <div id="vdo-calc-check" aria-live="polite">
          {check.ok && (
            <div className="vdo-cd-note ok"><Check size={15} aria-hidden="true" /><span>Looks right — it runs in the database, inside the visual’s query.</span></div>
          )}
          {!check.ok && !fxProblem && (
            <div className="vdo-cd-note info"><Info size={15} aria-hidden="true" /><span>Type a formula, pick a starter above, or click the fields and signs below the box.</span></div>
          )}
          {fxProblem && (
            <div className="vdo-cd-note err" role="alert">
              <AlertCircle size={15} aria-hidden="true" />
              <div className="vdo-cd-nb">
                <span>{fxProblem.message}</span>
                {mark && (
                  <div className="vdo-cd-mark" aria-label="Where the problem is">
                    {mark.before}<mark className={mark.gap ? 'gap' : ''}>{mark.gap ? '?' : mark.bad}</mark>{mark.after}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="vdo-cd-lbl">Work it out</div>
        <div className="vdo-cd-seg" role="radiogroup" aria-label="Work it out">
          {WHENS.map((w) => (
            <button key={w.id} type="button" role="radio" aria-checked={when === w.id} className={when === w.id ? 'on' : ''}
              disabled={readOnly} onClick={() => { setWhen(w.id); setFootError('') }}>{w.label}</button>
          ))}
        </div>
        <p className="vdo-cd-help">{whenHelp}</p>

        {check.ok && (
          <>
            <div className="vdo-cd-lbl">How it runs</div>
            <div className="vdo-cd-sql">{check.compiled.runs}</div>
          </>
        )}
      </div>
    </Modal>
  )
}
